import { Check, Copy, Download, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { DeleteDialog } from "@/components/delete-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAsync } from "@/hooks/use-async";
import { useAdminCourse, useAdminKey } from "@/hooks/use-auth";
import type { GeneratedKey, StudentKeyEntry, StudentRole } from "@/lib/api";
import { ApiError, adminApi } from "@/lib/api";
import { RoleSelect } from "@/components/role-select";
import { formatTimestamp } from "@/lib/format";

/** Parse "email,name" per line, tolerating a header row and bare emails. */
function parseRoster(text: string): { email: string; name: string }[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^email\s*,/i.test(line))
    .map((line) => {
      const [email, ...rest] = line.split(",");
      return { email: email.trim(), name: rest.join(",").trim() };
    })
    .filter((entry) => entry.email);
}

function GenerateDialog({
  open,
  onOpenChange,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const adminKey = useAdminKey();
  const courseId = useAdminCourse();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    created: GeneratedKey[];
    skipped: string[];
  } | null>(null);

  const entries = useMemo(() => parseRoster(text), [text]);

  async function submit() {
    if (!adminKey || !courseId || !entries.length) return;
    setBusy(true);
    try {
      const response = await adminApi.generateKeys(courseId, entries, adminKey);
      setResult(response);
      onDone();
      if (response.created.length) {
        toast.success(`Issued ${response.created.length} key(s)`);
      } else {
        toast.info("No new keys — every address already had one");
      }
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  function downloadCsv() {
    if (!result?.created.length) return;
    const csv = [
      "email,name,api_key",
      ...result.created.map((k) => `${k.email},${k.name},${k.key}`),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "api_keys.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  function close(next: boolean) {
    if (!next) {
      setText("");
      setResult(null);
    }
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Issue student keys</DialogTitle>
          <DialogDescription>
            One per line: <code className="font-mono">email,name</code>. Existing
            students are skipped.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <div className="text-sm">
              <p>
                <strong>{result.created.length}</strong> issued,{" "}
                <strong>{result.skipped.length}</strong> already existed.
              </p>
            </div>
            {result.created.length ? (
              <>
                <div className="max-h-64 overflow-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Key</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.created.map((k) => (
                        <TableRow key={k.email}>
                          <TableCell>{k.email}</TableCell>
                          <TableCell className="font-mono text-xs">
                            {k.key}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-muted-foreground text-xs">
                  Keys stay retrievable from this console.
                </p>
              </>
            ) : null}
            <DialogFooter>
              {result.created.length ? (
                <Button variant="outline" onClick={downloadCsv}>
                  <Download className="size-4" aria-hidden />
                  Download CSV
                </Button>
              ) : null}
              <Button onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="grid gap-2">
              <Label htmlFor="roster">Roster</Label>
              <textarea
                id="roster"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                spellCheck={false}
                placeholder={"ab1234@nyu.edu,Alice Brown\ncd5678@nyu.edu,Carl Diaz"}
                className="border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 w-full resize-y rounded-md border px-3 py-2 font-mono text-sm outline-none focus-visible:ring-3"
              />
              <p className="text-muted-foreground text-xs">
                {entries.length} student{entries.length === 1 ? "" : "s"} parsed
              </p>
            </div>
            <DialogFooter>
              <Button
                onClick={submit}
                disabled={busy || entries.length === 0}
              >
                Issue {entries.length || ""} key{entries.length === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KeyCell({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      className="font-mono text-xs"
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        toast.success("Key copied");
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? (
        <Check className="size-3" aria-hidden />
      ) : (
        <Copy className="size-3" aria-hidden />
      )}
      {value.slice(0, 10)}…
    </Button>
  );
}

export function AdminStudentsRoute() {
  const adminKey = useAdminKey();
  const courseId = useAdminCourse();
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState<StudentKeyEntry[] | null>(null);

  // Scoped to the selected course. The same address can be enrolled in several
  // courses and holds a separate key in each, so an unscoped list would show one
  // person several times with no indication of which key belongs where.
  const state = useAsync(
    adminKey && courseId ? () => adminApi.keys(adminKey, courseId) : null,
    [adminKey, courseId],
  );

  async function toggle(key: string, active: boolean) {
    if (!adminKey) return;
    try {
      await adminApi.patchKey(key, { active }, adminKey);
      toast.success(active ? "Key enabled" : "Key disabled");
      state.refetch();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    }
  }

  /**
   * Staff hold real keys to dry-run challenges; the role keeps their runs off the
   * leaderboard and out of the Slack sync's "who left". Flipping it re-ranks at the
   * next read; nothing is deleted.
   */
  async function setRole(key: string, role: StudentRole) {
    if (!adminKey) return;
    try {
      await adminApi.patchKey(key, { role }, adminKey);
      toast.success(role === "staff" ? "Marked as staff" : "Marked as student");
      state.refetch();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 sm:max-w-xs">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by email or name"
            className="pl-8"
          />
        </div>
        <Button onClick={() => setDialogOpen(true)} disabled={!courseId}>
          <Plus className="size-4" aria-hidden />
          Issue keys
        </Button>
      </div>

      <GenerateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onDone={state.refetch}
      />
      <DeleteStudentsDialog
        entries={deleting}
        courseId={courseId ?? ""}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setSelected(new Set());
          state.refetch();
        }}
      />

      <AsyncBoundary state={state}>
        {(keys) => {
          const needle = query.toLowerCase();
          const rows = keys
            .filter(
              (entry) =>
                !needle ||
                entry.email.toLowerCase().includes(needle) ||
                entry.name.toLowerCase().includes(needle),
            )
            .sort((a, b) => a.email.localeCompare(b.email));

          if (!rows.length) {
            return (
              <EmptyState
                title={query ? "No matches" : "Nobody enrolled yet"}
                description={undefined}
              />
            );
          }

          const activeCount = rows.filter((e) => e.active).length;
          const chosen = rows.filter((e) => selected.has(e.student_id));
          const allChosen = rows.length > 0 && chosen.length === rows.length;

          return (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-muted-foreground text-sm">
                  {rows.length} student{rows.length === 1 ? "" : "s"} · {activeCount}{" "}
                  active
                  {chosen.length ? ` · ${chosen.length} selected` : ""}
                </p>
                {chosen.length ? (
                  <Button variant="destructive" size="sm" onClick={() => setDeleting(chosen)}>
                    <Trash2 className="size-4" aria-hidden />
                    Delete {chosen.length} enrollment{chosen.length === 1 ? "" : "s"}
                  </Button>
                ) : null}
              </div>
              <div className="overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40">
                      <TableHead className="w-10">
                        <Checkbox
                          aria-label="Select all shown"
                          checked={allChosen}
                          indeterminate={chosen.length > 0 && !allChosen}
                          onCheckedChange={(next) =>
                            setSelected(next === true ? new Set(rows.map((e) => e.student_id)) : new Set())
                          }
                        />
                      </TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Issued</TableHead>
                      <TableHead>Key</TableHead>
                      <TableHead className="text-right">Active</TableHead>
                      <TableHead className="w-10">
                        <span className="sr-only">Delete</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((entry) => (
                      <TableRow key={entry.key} data-state={selected.has(entry.student_id) ? "selected" : undefined}>
                        <TableCell>
                          <Checkbox
                            aria-label={`Select ${entry.email}`}
                            checked={selected.has(entry.student_id)}
                            onCheckedChange={() =>
                              setSelected((prev) => {
                                const next = new Set(prev);
                                if (next.has(entry.student_id)) next.delete(entry.student_id);
                                else next.add(entry.student_id);
                                return next;
                              })
                            }
                          />
                        </TableCell>
                        <TableCell className="font-medium">{entry.email}</TableCell>
                        <TableCell>
                          {entry.name || (
                            <span className="text-muted-foreground text-sm">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <RoleSelect
                            value={entry.role}
                            onChange={(role) => setRole(entry.key, role)}
                            label={`Role of ${entry.email}`}
                          />
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatTimestamp(entry.created_at)}
                        </TableCell>
                        <TableCell>
                          <KeyCell value={entry.key} />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {!entry.active ? (
                              <Badge variant="secondary">disabled</Badge>
                            ) : null}
                            <Switch
                              checked={entry.active}
                              onCheckedChange={(next) => toggle(entry.key, next)}
                              aria-label={`Toggle ${entry.email}`}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-destructive"
                            aria-label={`Delete ${entry.email}`}
                            title="Delete this enrollment"
                            onClick={() => setDeleting([entry])}
                          >
                            <Trash2 className="size-4" aria-hidden />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          );
        }}
      </AsyncBoundary>
    </div>
  );
}


/**
 * Remove one or many enrollments from this course.
 *
 * The case this exists for is the Slack sync run against the wrong course: sixty rows
 * with keys nobody should hold. Disabling the keys would leave the rows on the roster
 * and in "left" forever; deleting is the honest fix. It is per enrollment, so the same
 * people in the right course are untouched.
 *
 * One person: the server's 428 sentence says what goes and the email is typed back.
 * Many: the list is shown, and the course id is typed back, since that is what the
 * operator is really confirming -- "yes, *this* course".
 */
function DeleteStudentsDialog({
  entries,
  courseId,
  onClose,
  onDeleted,
}: {
  entries: StudentKeyEntry[] | null;
  courseId: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const adminKey = useAdminKey();
  const single = entries?.length === 1 ? entries[0] : null;
  const preview = useAsync(
    single && adminKey ? () => adminApi.previewDeleteStudent(single.student_id, adminKey) : null,
    [single?.student_id, adminKey],
  );
  const many = entries && entries.length > 1 ? entries : null;

  return (
    <DeleteDialog
      open={entries !== null}
      onOpenChange={(next) => !next && onClose()}
      title={single ? `Delete ${single.email}?` : `Delete ${entries?.length ?? 0} enrollments from ${courseId}?`}
      description={
        single
          ? preview.data
          : `Each of these enrollments is removed with its keys, submissions and simulation sessions. Their enrollments in other courses are untouched.`
      }
      loadingDescription={Boolean(single) && preview.loading}
      confirmToken={single ? single.email : courseId}
      confirmLabel={many ? `Delete ${many.length} enrollments` : "Delete permanently"}
      onConfirm={async () => {
        if (!adminKey || !entries) return;
        const failed: string[] = [];
        for (const entry of entries) {
          try {
            await adminApi.deleteStudent(entry.student_id, entry.email, adminKey);
          } catch (caught) {
            failed.push(`${entry.email}: ${caught instanceof ApiError ? caught.message : "failed"}`);
          }
        }
        if (failed.length) {
          toast.error(`${failed.length} not deleted`, { description: failed.join("\n"), duration: 15000 });
        }
        toast.success(`Deleted ${entries.length - failed.length}`);
      }}
      onDone={onDeleted}
    >
      {many ? (
        <ul className="max-h-48 overflow-y-auto rounded-md border p-2 font-mono text-xs">
          {many.map((e) => (
            <li key={e.student_id} className="flex justify-between gap-3">
              <span>{e.email}</span>
              <span className="text-muted-foreground truncate">{e.name}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground rounded-md border border-dashed p-3 text-xs">
          To keep their record, disable the key instead: they cannot submit, and their past submissions stay attributable.
        </p>
      )}
    </DeleteDialog>
  );
}
